/**
 * Rollup Factor set model (EAINT-877). Authoring-only — compiled down to
 * `populationSources[] + exclusions[]` on save (Phase 5). Not the payload shape itself.
 */
import type { FilterExpression, FilterGroup, PerspectiveFilter } from 'types/filters';
import type { MonitoringRules, PopulationSource, RollupExclusion } from 'types/spherePoints';

export type RollupSetScope = 'related' | 'any';

/**
 * Engine vocabulary at the state boundary (D51 — `validatePopulationSources.ts` accepts only
 * `'inbound' | 'outbound'`). `null` means the question doesn't apply — scope is `any`, so there
 * is nothing to traverse. `'both'` is a direction-card label only pre-EAINT-937: it is never a
 * value this type can hold, because the engine rejects it by name.
 */
export type RollupSetDirection = 'inbound' | 'outbound' | null;

/**
 * The built-in hierarchy perspective START FROM traverses (parent/child) — the same fallback
 * `resolveRoster.ts`'s `normalizePopulationSources` applies to a legacy factor with no
 * `perspectiveSystemName`. START FROM has no perspective picker of its own, so a rule source this
 * card writes names it explicitly rather than relying on that fallback.
 */
export const ROLLUP_SET_HIERARCHY_PERSPECTIVE = 'center_parent';

export interface RollupSetModel {
  scope: RollupSetScope;
  direction: RollupSetDirection;
  /**
   * The last real direction the author picked, remembered across a trip through `any` —
   * `direction` itself goes to `null` while scope is `any`, so switching back to `related`
   * needs somewhere else to read the prior choice from (restores it instead of resetting to
   * `outbound`).
   */
  lastDirection: 'inbound' | 'outbound';
  /**
   * The perspective the rule source traverses (`center_parent` by default — the hierarchy edge
   * the set card is designed around). Preserved so a legacy factor authored against a different
   * perspective (e.g. `center_responsible`) round-trips without being silently rewritten to the
   * hierarchy. Not exposed in the set card UI today; carried on the model so `decompileSet` and
   * `compileSet` can hand it through.
   */
  perspectiveSystemName: string;
  tree: FilterGroup;
  pins: string[];
  authorExcluded: string[];
  /**
   * Legacy `monitoringRules.filters` shapes the set card cannot yet author into the tree
   * (`perspectiveFilters`, `propertyFilters` as flat arrays). Ride-along storage so a pre-EAINT-875
   * factor's filter surface isn't dropped when the card decompiles and re-compiles it. Never
   * populated by `compileSet`'s own output — only by `decompileSet` from a legacy source.
   */
  preservedLegacyFilters?: {
    perspectiveFilters?: PerspectiveFilter[];
    propertyFilters?: FilterExpression[];
  };
}

export const defaultRollupSet = (): RollupSetModel => ({
  scope: 'related',
  direction: 'outbound',
  lastDirection: 'outbound',
  perspectiveSystemName: ROLLUP_SET_HIERARCHY_PERSPECTIVE,
  tree: { expressions: [], operator: 'and' },
  pins: [],
  authorExcluded: [],
});

export function setScope(set: RollupSetModel, scope: RollupSetScope): RollupSetModel {
  if (scope === set.scope) return set;
  return {
    ...set,
    scope,
    direction: scope === 'any' ? null : set.lastDirection,
  };
}

export function setDirection(set: RollupSetModel, direction: 'inbound' | 'outbound'): RollupSetModel {
  return { ...set, direction, lastDirection: direction };
}

export function setTree(set: RollupSetModel, tree: FilterGroup): RollupSetModel {
  return { ...set, tree };
}

export function addPin(set: RollupSetModel, sphereId: string): RollupSetModel {
  if (set.pins.includes(sphereId)) return set;
  return { ...set, pins: [...set.pins, sphereId] };
}

export function removePin(set: RollupSetModel, sphereId: string): RollupSetModel {
  if (!set.pins.includes(sphereId)) return set;
  return { ...set, pins: set.pins.filter((id) => id !== sphereId) };
}

/**
 * The set's resolved membership — tree-matched ids (server-evaluated; supplied by the
 * caller, never computed here) unioned with pins, deduped by id. A sphere both matched
 * and pinned appears once.
 */
export function resolveMembers(set: RollupSetModel, matchedIds: string[]): string[] {
  return Array.from(new Set([...matchedIds, ...set.pins]));
}

export function hasConditions(tree: FilterGroup): boolean {
  return (tree.expressions?.length ?? 0) > 0 || (tree.groups?.length ?? 0) > 0;
}

/**
 * Compiles the authored set down to `populationSources[]` (EAINT-877 Phase 5) — the shape
 * `validatePopulationSources` accepts and `resolveRoster`/`matchesFactorPopulation` evaluate.
 * `authorExcluded` never compiles here: exclusions are server-stamped (D56), owned by the roster
 * exclude/restore and PATCH reason routes, and only read back on decompile. `scope: 'any'` has no rule-source representation yet (D58/EAINT-937) so it compiles
 * to pins only, same as an author who has added nothing.
 */
export function compileSet(set: RollupSetModel): PopulationSource[] {
  const sources: PopulationSource[] = [];

  if (set.scope === 'related' && set.direction) {
    // Merge the compiled tree (if any) with legacy flat filters the card cannot yet author, so a
    // pre-EAINT-875 factor re-saves without silently dropping its `perspectiveFilters`/
    // `propertyFilters`. Set-card writes always populate the tree; legacy factors always populate
    // `preservedLegacyFilters` at decompile time — the two never overlap in practice.
    const treeGroup = hasConditions(set.tree) ? { filterGroups: [set.tree] } : {};
    const legacyFlat = set.preservedLegacyFilters ?? {};
    const compiledFilters = { ...treeGroup, ...legacyFlat };
    const hasAnyFilters = Object.keys(compiledFilters).length > 0;
    sources.push({
      kind: 'rule',
      direction: set.direction,
      perspectiveSystemName: set.perspectiveSystemName,
      ...(hasAnyFilters ? { filters: compiledFilters } : {}),
    });
  }

  if (set.pins.length > 0) {
    sources.push({ kind: 'direct' });
  }

  return sources;
}

/**
 * Reverses `compileSet` — reconstructs the four bands from a saved factor's
 * `populationSources[]` + `exclusions[]`. Pins are never read out of `populationSources`: a
 * `direct` source carries no member ids (D50), so the caller supplies them from the members
 * route response.
 *
 * `legacyMonitoringRules` is read only when `populationSources` is absent or empty — a
 * pre-EAINT-875 factor that never compiled down to the new shape. `populationSources` wins the
 * moment it exists at all (even a pins-only `[{ kind: 'direct' }]`), matching `resolveRoster.ts`'s
 * read-time precedence, so a factor already authored via the set card never falls back here.
 */
export function decompileSet(
  populationSources: PopulationSource[] | undefined,
  pinnedSphereIds: string[],
  exclusions: RollupExclusion[] | undefined,
  legacyMonitoringRules?: MonitoringRules
): RollupSetModel {
  const base = defaultRollupSet();
  const ruleSource = populationSources?.find(
    (source): source is Extract<PopulationSource, { kind: 'rule' }> => source.kind === 'rule'
  );
  const legacyRule = populationSources?.length ? undefined : legacyMonitoringRules;
  const direction = ruleSource?.direction ?? legacyRule?.direction;
  const filters = ruleSource?.filters ?? legacyRule?.filters as
    | { filterGroups?: FilterGroup[]; perspectiveFilters?: PerspectiveFilter[]; propertyFilters?: FilterExpression[] }
    | undefined;
  const tree = filters?.filterGroups?.[0];
  // Legacy `monitoringRules.filters` can carry a top-level `perspectiveFilters` or
  // `propertyFilters` array with no tree — shapes the set card cannot yet author. Preserve them
  // verbatim so `compileSet` re-emits them alongside the tree it does own.
  const preservedLegacyFilters =
    filters && ((filters.perspectiveFilters?.length ?? 0) > 0 || (filters.propertyFilters?.length ?? 0) > 0)
      ? {
          ...(filters.perspectiveFilters?.length ? { perspectiveFilters: filters.perspectiveFilters } : {}),
          ...(filters.propertyFilters?.length ? { propertyFilters: filters.propertyFilters } : {}),
        }
      : undefined;
  // Perspective: prefer explicit rule-source, then legacy top-level, then the first legacy
  // `perspectiveFilters` entry (mirrors `readRuleSource` in `useStatusFactorsState.ts`), then the
  // hierarchy default.
  const perspectiveSystemName =
    ruleSource?.perspectiveSystemName
    ?? legacyRule?.perspectiveSystemName
    ?? (legacyRule?.filters?.perspectiveFilters?.[0] as { perspectiveSystemName?: string } | undefined)?.perspectiveSystemName
    ?? base.perspectiveSystemName;

  return {
    ...base,
    direction: direction ?? base.direction,
    lastDirection: direction ?? base.lastDirection,
    perspectiveSystemName,
    tree: tree ?? base.tree,
    pins: pinnedSphereIds,
    authorExcluded: (exclusions ?? []).map((exclusion) => exclusion.sphereId),
    ...(preservedLegacyFilters ? { preservedLegacyFilters } : {}),
  };
}
